import { Icon } from "@common/index";

interface CartItemStepperProps {
    value: number;
    onChange: (next: number) => void;
    onRemove: () => void;
    min?: number;
    max?: number;
    showRemove?: boolean;
    // The line's quantity can't change: the quantity controls are disabled. Remove is not.
    disabled?: boolean;
    // The id of the element that says why (aria-describedby on each quantity control).
    describedBy?: string;
}

export const CartItemStepper = ({
    value,
    onChange,
    onRemove,
    min = 1,
    max,
    showRemove = true,
    disabled = false,
    describedBy,
}: CartItemStepperProps) => {
    const clamp = (n: number) => {
        if (Number.isNaN(n)) return min;
        if (n < min) return min;
        if (max !== undefined && n > max) return max;
        return n;
    };

    const handleDecrease = () => onChange(clamp(value - 1));
    const handleIncrease = () => onChange(clamp(value + 1));

    return (
        <div className="flex items-center justify-between gap-1 rounded-md border border-info bg-info/20 px-2 py-1">
            {showRemove && (
                <button
                    type="button"
                    aria-label="Remove item"
                    onClick={onRemove}
                    className="btn btn-ghost btn-sm btn-square text-primary"
                >
                    <Icon name="delete" className="text-lg" />
                </button>
            )}
            <button
                type="button"
                aria-label="Decrease quantity"
                aria-describedby={describedBy}
                onClick={handleDecrease}
                disabled={disabled || value <= min}
                className="btn btn-ghost btn-sm btn-square text-neutral"
            >
                <Icon name="remove" className="text-lg" />
            </button>
            <input
                type="number"
                role="spinbutton"
                aria-label="Quantity"
                aria-describedby={describedBy}
                disabled={disabled}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
                className="w-12 bg-transparent text-center font-semibold text-neutral focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
                type="button"
                aria-label="Increase quantity"
                aria-describedby={describedBy}
                onClick={handleIncrease}
                disabled={disabled || (max !== undefined && value >= max)}
                className="btn btn-ghost btn-sm btn-square text-primary"
            >
                <Icon name="add" className="text-lg" />
            </button>
        </div>
    );
};
